import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
import type { Property } from '../lib/types';
import { AUTH_ENABLED } from '../lib/auth';
const noopGetToken = async () => null;

export function useProperties(
  profileId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProperties = useCallback(async () => {
    if (!AUTH_ENABLED || !profileId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Property[]>(`/profiles/${profileId}/properties`, {}, getToken);
      setProperties(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileId, getToken]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  // Keep a ref so createProperty can check the current list without needing
  // `properties` in its dependency array (which would cause unnecessary re-creation).
  const propertiesRef = useRef<Property[]>([]);
  propertiesRef.current = properties;

  const createProperty = useCallback(async (body: {
    address: string;
    city: string;
    zip: string;
    county: string;
    askingPrice: number;
    notes?: string;
  }): Promise<{ property: Property; alreadySaved: boolean }> => {
    const prop = await apiFetch<Property>(`/profiles/${profileId}/properties`, {
      method: 'POST',
      body: JSON.stringify(body),
    }, getToken);
    // The API silently deduplicates — if this property ID is already in the list
    // (added by this user or another member of the same profile), don't append it
    // again (would cause a React duplicate-key error).
    const alreadySaved = propertiesRef.current.some(p => p.id === prop.id);
    if (!alreadySaved) {
      setProperties(prev => [...prev, prop]);
    }
    return { property: prop, alreadySaved };
  }, [profileId, getToken]);

  const updateProperty = useCallback(async (propId: string, body: Partial<Property>): Promise<Property> => {
    const prop = await apiFetch<Property>(`/profiles/${profileId}/properties/${propId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, getToken);
    setProperties(prev => prev.map(p => p.id === propId ? prop : p));
    return prop;
  }, [profileId, getToken]);

  const deleteProperty = useCallback(async (propId: string) => {
    await apiFetch(`/profiles/${profileId}/properties/${propId}`, { method: 'DELETE' }, getToken);
    setProperties(prev => prev.filter(p => p.id !== propId));
  }, [profileId, getToken]);

  return { properties, loading, error, fetchProperties, createProperty, updateProperty, deleteProperty };
}

export function usePropertyDetail(
  profileId: string | undefined,
  propertyId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProperty = useCallback(async () => {
    if (!AUTH_ENABLED || !profileId || !propertyId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Property>(`/profiles/${profileId}/properties/${propertyId}`, {}, getToken);
      setProperty(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileId, propertyId, getToken]);

  useEffect(() => { fetchProperty(); }, [fetchProperty]);

  return { property, loading, error, refetch: fetchProperty };
}
